import { trackAppEvent } from "./appEventsService";

export const getPracticeWeek = (): string => {
  const now = new Date();
  const monday = new Date(now);
  const day = now.getDay();
  monday.setDate(now.getDate() - ((day + 6) % 7));
  monday.setHours(0, 0, 0, 0);
  return monday.toISOString();
};

type EventData = Record<string, unknown>;

const sendPracticeEvent = (
  accessToken: string | null | undefined,
  eventName: string,
  data: EventData = {}
) => {
  void trackAppEvent({
    accessToken,
    eventName,
    data: { ...data, week: getPracticeWeek() }
  });
};

export const trackPracticeEntryPointClicked = (
  accessToken: string | null | undefined,
  source: string
) => sendPracticeEvent(accessToken, "practice entry point clicked", { source });

export const trackPracticeScreenViewed = (
  accessToken: string | null | undefined
) => sendPracticeEvent(accessToken, "practice screen viewed");

export const trackPracticeStarted = (accessToken: string | null | undefined) =>
  sendPracticeEvent(accessToken, "practice started");

export const trackPracticeContinue = (accessToken: string | null | undefined) =>
  sendPracticeEvent(accessToken, "practice continue");

export const trackPracticeQuestionOpened = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number
) =>
  sendPracticeEvent(accessToken, "practice question opened", {
    question_id: questionId,
    question_number: questionNumber
  });

export const trackPracticeHintOpened = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number
) =>
  sendPracticeEvent(accessToken, "practice hint opened", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt
  });

export const trackPracticeHowToSolveOpened = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number
) =>
  sendPracticeEvent(accessToken, "practice how to solve opened", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt
  });

export const trackPracticeSessionExited = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number
) =>
  sendPracticeEvent(accessToken, "practice session exited", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt
  });

export const trackPracticeAnswerImageSubmitted = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number,
  isCorrectAnswer: boolean
) =>
  sendPracticeEvent(accessToken, "practice answer image submitted", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt,
    is_correct_answer: isCorrectAnswer
  });

export const trackPracticeExplanationViewed = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number,
  isCorrectAnswer: boolean
) =>
  sendPracticeEvent(accessToken, "practice explanation viewed", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt,
    is_correct_answer: isCorrectAnswer
  });

export const trackPracticeMcqAttempted = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number,
  isCorrectAnswer: boolean,
  selectedAnswerId: string
) =>
  sendPracticeEvent(accessToken, "practice mcq attempted", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt,
    is_correct_answer: isCorrectAnswer,
    selected_answer_id: selectedAnswerId
  });

export const trackPracticeQuestionCompleted = (
  accessToken: string | null | undefined,
  questionId: string,
  questionNumber: number,
  answerAttempt: number,
  finalAttemptResult: string
) =>
  sendPracticeEvent(accessToken, "practice question completed", {
    question_id: questionId,
    question_number: questionNumber,
    answer_attempt: answerAttempt,
    final_attempt_result: finalAttemptResult
  });

export const trackPracticeCompleted = (
  accessToken: string | null | undefined,
  totalPoints: number,
  correctAnswerPoints: number,
  effortPoints: number
) =>
  sendPracticeEvent(accessToken, "practice completed", {
    total_points: totalPoints,
    correct_answer_points: correctAnswerPoints,
    effort_points: effortPoints
  });

export const trackPracticeRankedViewed = (
  accessToken: string | null | undefined,
  leaderboardOption: string,
  viewingOtherRanks: boolean
) =>
  sendPracticeEvent(
    accessToken,
    viewingOtherRanks
      ? "practice other ranked viewed"
      : "practice my ranked viewed",
    { leaderboard_option: leaderboardOption }
  );

export const trackPracticeHistoryViewed = (
  accessToken: string | null | undefined,
  currentWeekPracticeStatus: string
) =>
  sendPracticeEvent(accessToken, "practice history viewed", {
    current_week_practice_status: currentWeekPracticeStatus
  });

export const trackPracticeError = (
  accessToken: string | null | undefined,
  screen: string,
  reason: string,
  action: string
) =>
  sendPracticeEvent(accessToken, "practice error", {
    screen,
    reason,
    action
  });

export const trackPracticeAudioPlay = (
  accessToken: string | null | undefined,
  id: string,
  type: "practice_attempt",
  source: "answer_analysis" | "history",
  attempt: "first_answer_analysis" | "second_answer_analysis"
) =>
  sendPracticeEvent(accessToken, "practice audio play clicked", {
    id,
    type,
    source,
    attempt
  });

export const trackPracticeAudioStreamingStarted = (
  accessToken: string | null | undefined,
  source: "first_answer_analysis" | "second_answer_analysis",
  screen: string
) =>
  sendPracticeEvent(accessToken, "practice audio streaming started", {
    source,
    screen
  });

export const trackPracticeAudioStreamingStopped = (
  accessToken: string | null | undefined,
  source: "first_answer_analysis" | "second_answer_analysis",
  screen: string,
  type: string,
  id: string,
  reason: string,
  data: {
    streamingDuration: number;
    audioDuration: number;
    bufferedDuration: number;
    receivedChunks: number;
    totalChunks: number;
  }
) =>
  sendPracticeEvent(accessToken, "practice audio streaming stopped", {
    source,
    screen,
    type,
    id,
    reason,
    streamingDuration: data.streamingDuration,
    audioDuration: data.audioDuration,
    bufferedDuration: data.bufferedDuration,
    receivedChunks: data.receivedChunks,
    totalChunks: data.totalChunks
  });
